import { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useLanguage } from '../contexts/LanguageContext';
import { api, formatBytes, formatDate } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Scan,
  CloudArrowUp,
  FileText,
  Eye,
  Receipt,
  CheckCircle,
  X,
  PencilSimple,
  Archive,
  SpinnerGap,
  CaretRight,
  MagnifyingGlass,
  FilePdf,
  FileImage,
  Truck,
  CurrencyCircleDollar,
  ShoppingCart,
  ArrowCounterClockwise,
  File as FileIcon,
  Tag as TagIcon
} from '@phosphor-icons/react';

// Document type styling helper
const getDocumentTypeStyle = (type) => {
  const styles = {
    fattura: { icon: Receipt, color: 'text-blue-600', bg: 'bg-blue-50' },
    contratto: { icon: FileText, color: 'text-purple-600', bg: 'bg-purple-50' },
    ddt: { icon: Truck, color: 'text-orange-600', bg: 'bg-orange-50' },
    preventivo: { icon: CurrencyCircleDollar, color: 'text-green-600', bg: 'bg-green-50' },
    ordine: { icon: ShoppingCart, color: 'text-cyan-600', bg: 'bg-cyan-50' },
    nota_credito: { icon: ArrowCounterClockwise, color: 'text-red-600', bg: 'bg-red-50' },
    altro: { icon: FileIcon, color: 'text-gray-500', bg: 'bg-gray-50' }
  };
  return styles[type] || styles.altro;
};

const getFileIcon = (contentType) => {
  if (contentType?.includes('pdf')) return FilePdf;
  if (contentType?.includes('image')) return FileImage;
  return FileText;
};

export const ScannerPage = () => {
  const { t, language } = useLanguage();
  const [folders, setFolders] = useState([]);
  const [scannedDocs, setScannedDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  
  // Form state
  const [selectedFolder, setSelectedFolder] = useState('none');
  const [tags, setTags] = useState('');
  const [ocrLanguage, setOcrLanguage] = useState(language);
  const [extractInvoice, setExtractInvoice] = useState(true);
  
  // Detail view
  const [viewDoc, setViewDoc] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(false);
  const [editedInvoiceData, setEditedInvoiceData] = useState({});
  const [saving, setSaving] = useState(false);

  const localeMap = { it: 'it-CH', de: 'de-CH', fr: 'fr-CH', en: 'en-GB' };

  const fetchFolders = async () => {
    try {
      const res = await api.getAllFolders();
      setFolders(res.data);
    } catch (error) {
      console.error('Failed to fetch folders:', error);
    }
  };

  const fetchScannedDocs = async () => {
    try {
      setLoading(true);
      const res = await api.getScannedDocuments();
      setScannedDocs(res.data);
    } catch (error) {
      console.error('Failed to fetch scanned documents:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFolders();
    fetchScannedDocs();
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      setOcrResult(null);
      
      // Create preview URL
      if (file.type.startsWith('image/') || file.type === 'application/pdf') {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.tiff', '.bmp', '.gif'],
      'application/pdf': ['.pdf']
    },
    multiple: false
  });

  const handlePreviewOcr = async () => {
    if (!selectedFile) return;
    
    setProcessing(true);
    try {
      const res = await api.ocrPreview(selectedFile, ocrLanguage, extractInvoice);
      setOcrResult(res.data);
    } catch (error) {
      toast.error(t('scanner.scanError'));
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveDocument = async () => {
    if (!selectedFile) return;
    
    setProcessing(true);
    try {
      const folderId = selectedFolder === 'none' ? null : selectedFolder;
      await api.processScannedDocument(selectedFile, folderId, tags, ocrLanguage, extractInvoice);
      toast.success(t('scanner.scanSuccess'));
      
      // Reset form
      setSelectedFile(null);
      setPreviewUrl(null);
      setOcrResult(null);
      setTags('');
      setSelectedFolder('none');
      
      // Refresh list
      fetchScannedDocs();
    } catch (error) {
      toast.error(t('scanner.scanError'));
    } finally {
      setProcessing(false);
    }
  };

  const handleNewScan = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setOcrResult(null);
    setTags('');
  };

  const openViewDoc = async (doc) => {
    try {
      const res = await api.getScannedDocument(doc.id);
      setViewDoc(res.data);
      setEditedInvoiceData(res.data.invoice_data || {});
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleSaveInvoiceData = async () => {
    if (!viewDoc) return;
    
    setSaving(true);
    try {
      await api.updateInvoiceData(viewDoc.id, editedInvoiceData);
      toast.success(t('common.success'));
      setEditingInvoice(false);
      // Refresh doc
      openViewDoc(viewDoc);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const renderInvoiceField = (fieldKey, value) => {
    const label = t(`scanner.invoiceFields.${fieldKey}`) || fieldKey;
    
    if (editingInvoice) {
      return (
        <div key={fieldKey} className="space-y-1">
          <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
            {label}
          </Label>
          <Input
            value={editedInvoiceData[fieldKey] || ''}
            onChange={(e) => setEditedInvoiceData(prev => ({
              ...prev,
              [fieldKey]: e.target.value
            }))}
            className="input-swiss"
          />
        </div>
      );
    }
    
    return (
      <div key={fieldKey} className="flex justify-between py-2 border-b border-[#E4E4E7]">
        <span className="text-sm text-[#52525B]">{label}</span>
        <span className="text-sm font-medium text-[#09090B]">
          {value || '—'}
        </span>
      </div>
    );
  };

  return (
    <Layout>
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title" data-testid="scanner-title">
              <Scan size={28} className="inline mr-2" />
              {t('scanner.title')}
            </h1>
            <p className="text-sm text-[#52525B] mt-1">{t('scanner.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="content-area">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upload Section */}
          <Card className="border-[#E4E4E7]">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <CloudArrowUp size={20} />
                {t('scanner.uploadScan')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Dropzone */}
              {!selectedFile ? (
                <div
                  {...getRootProps()}
                  className={`dropzone p-8 text-center cursor-pointer border-2 border-dashed rounded-sm transition-colors ${
                    isDragActive ? 'border-[#002FA7] bg-[#E3F2FD]' : 'border-[#E4E4E7]'
                  }`}
                  data-testid="scanner-dropzone"
                >
                  <input {...getInputProps()} data-testid="scanner-file-input" />
                  <Scan size={48} className="mx-auto mb-3 text-[#A1A1AA]" />
                  <p className="text-sm text-[#52525B]">
                    {isDragActive ? t('scanner.dropzoneActive') : t('scanner.dropzone')}
                  </p>
                  <p className="text-xs text-[#A1A1AA] mt-2">
                    PDF, JPG, PNG, TIFF
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Preview */}
                  <div className="bg-[#F4F4F5] rounded-sm p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {selectedFile.type.includes('pdf') ? (
                          <FilePdf size={24} className="text-[#E32636]" />
                        ) : (
                          <FileImage size={24} className="text-[#002FA7]" />
                        )}
                        <div>
                          <p className="text-sm font-medium text-[#09090B]">{selectedFile.name}</p>
                          <p className="text-xs text-[#52525B]">{formatBytes(selectedFile.size)}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleNewScan}
                        className="text-[#E32636] hover:bg-[#FFEBEE]"
                      >
                        <X size={18} />
                      </Button>
                    </div>
                    
                    {previewUrl && selectedFile.type.startsWith('image/') && (
                      <img 
                        src={previewUrl} 
                        alt="Preview" 
                        className="max-h-48 mx-auto rounded-sm"
                      />
                    )}
                  </div>

                  {/* OCR Settings */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                        {t('scanner.ocrLanguage')}
                      </Label>
                      <Select value={ocrLanguage} onValueChange={setOcrLanguage}>
                        <SelectTrigger className="mt-1 rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="it">Italiano</SelectItem>
                          <SelectItem value="de">Deutsch</SelectItem>
                          <SelectItem value="fr">Français</SelectItem>
                          <SelectItem value="en">English</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                        {t('scanner.selectFolder')}
                      </Label>
                      <Select value={selectedFolder} onValueChange={setSelectedFolder}>
                        <SelectTrigger className="mt-1 rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t('folders.root')}</SelectItem>
                          {[...folders].sort((a, b) => a.name.localeCompare(b.name)).map((folder) => (
                            <SelectItem key={folder.id} value={folder.id}>
                              {folder.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-[#52525B]">
                      <Receipt size={16} className="inline mr-1" />
                      {t('scanner.extractInvoice')}
                    </Label>
                    <Switch
                      checked={extractInvoice}
                      onCheckedChange={setExtractInvoice}
                      data-testid="extract-invoice-toggle"
                    />
                  </div>

                  <div>
                    <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                      {t('scanner.tags')}
                    </Label>
                    <Input
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      placeholder={t('documents.tagsPlaceholder')}
                      className="input-swiss mt-1"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      onClick={handlePreviewOcr}
                      disabled={processing}
                      variant="outline"
                      className="flex-1 rounded-sm"
                      data-testid="preview-ocr-btn"
                    >
                      {processing ? (
                        <SpinnerGap size={18} className="mr-2 animate-spin" />
                      ) : (
                        <MagnifyingGlass size={18} className="mr-2" />
                      )}
                      {t('scanner.preview')}
                    </Button>
                    <Button
                      onClick={handleSaveDocument}
                      disabled={processing}
                      className="flex-1 bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
                      data-testid="save-scan-btn"
                    >
                      {processing ? (
                        <SpinnerGap size={18} className="mr-2 animate-spin" />
                      ) : (
                        <Archive size={18} className="mr-2" />
                      )}
                      {t('scanner.saveDocument')}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* OCR Results Section */}
          <Card className="border-[#E4E4E7]">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <FileText size={20} />
                {t('scanner.ocrResult')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {processing ? (
                <div className="text-center py-12">
                  <SpinnerGap size={40} className="mx-auto mb-3 text-[#002FA7] animate-spin" />
                  <p className="text-sm text-[#52525B]">{t('scanner.processing')}</p>
                </div>
              ) : ocrResult ? (
                <div className="space-y-4">
                  {/* Classification Result */}
                  {ocrResult.classification && (
                    <div className="p-3 bg-[#F4F4F5] rounded-sm">
                      <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-2 block">
                        {t('documents.classification')}
                      </Label>
                      {(() => {
                        const typeStyle = getDocumentTypeStyle(ocrResult.classification.type_label);
                        const TypeIcon = typeStyle.icon;
                        return (
                          <div className="flex items-center gap-3">
                            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm ${typeStyle.bg}`}>
                              <TypeIcon size={18} className={typeStyle.color} weight="fill" />
                              <span className={`text-sm font-medium ${typeStyle.color}`}>
                                {t(`documents.types.${ocrResult.classification.type_label}`)}
                              </span>
                            </div>
                            <span className="text-sm text-[#52525B]">
                              {t('documents.confidence')}: <strong>{Math.round(ocrResult.classification.confidence)}%</strong>
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  
                  {/* Extracted Text */}
                  <div>
                    <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-2 block">
                      {t('scanner.ocrResult')} ({ocrResult.text_length} chars)
                    </Label>
                    <Textarea
                      value={ocrResult.ocr_text || t('scanner.noTextFound')}
                      readOnly
                      className="h-32 text-xs font-mono bg-[#F4F4F5]"
                    />
                  </div>

                  {/* Invoice Data */}
                  {extractInvoice && ocrResult.invoice_data && (
                    <div>
                      <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-2 block">
                        <Receipt size={14} className="inline mr-1" />
                        {t('scanner.invoiceData')}
                      </Label>
                      <div className="bg-[#F4F4F5] rounded-sm p-3 space-y-1 text-sm">
                        {Object.entries(ocrResult.invoice_data)
                          .filter(([key]) => key !== 'raw_text')
                          .map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="text-[#52525B]">
                                {t(`scanner.invoiceFields.${key}`) || key}
                              </span>
                              <span className="font-medium">
                                {value !== null ? String(value) : '—'}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-[#A1A1AA]">
                  <Eye size={40} className="mx-auto mb-3" />
                  <p className="text-sm">{t('scanner.noTextFound')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Scans */}
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-[#09090B] mb-4">
            {t('scanner.recentScans')}
          </h2>
          
          {loading ? (
            <div className="text-center py-8 text-[#52525B]">
              {t('common.loading')}
            </div>
          ) : scannedDocs.length === 0 ? (
            <div className="text-center py-8 text-[#A1A1AA]">
              <Scan size={40} className="mx-auto mb-3" />
              <p>{t('common.noData')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {scannedDocs.slice(0, 9).map((doc) => {
                const Icon = getFileIcon(doc.content_type);
                const hasInvoiceData = doc.invoice_data && Object.keys(doc.invoice_data).length > 0;
                
                return (
                  <Card 
                    key={doc.id} 
                    className="border-[#E4E4E7] cursor-pointer hover:border-[#002FA7] transition-colors"
                    onClick={() => openViewDoc(doc)}
                    data-testid={`scan-card-${doc.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="document-icon bg-[#E3F2FD] p-2 rounded-sm">
                          <Icon size={24} className="text-[#002FA7]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#09090B] truncate">
                            {doc.original_filename}
                          </p>
                          <p className="text-xs text-[#52525B]">
                            {formatDate(doc.created_at, localeMap[language])}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            {doc.has_pdfa && (
                              <Badge className="bg-green-100 text-green-700 text-[10px]">
                                PDF/A
                              </Badge>
                            )}
                            {doc.document_type_label && doc.document_type_label !== 'altro' && (() => {
                              const typeStyle = getDocumentTypeStyle(doc.document_type_label);
                              const TypeIcon = typeStyle.icon;
                              return (
                                <Badge className={`${typeStyle.bg} ${typeStyle.color} text-[10px]`}>
                                  <TypeIcon size={10} className="mr-1" />
                                  {t(`documents.types.${doc.document_type_label}`)}
                                </Badge>
                              );
                            })()}
                          </div>
                          {hasInvoiceData && doc.invoice_data.total_amount && (
                            <p className="text-sm font-semibold text-[#09090B] mt-2">
                              {doc.invoice_data.currency || 'CHF'} {doc.invoice_data.total_amount}
                            </p>
                          )}
                        </div>
                        <CaretRight size={16} className="text-[#A1A1AA]" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* View Document Dialog */}
      <Dialog open={!!viewDoc} onOpenChange={() => { setViewDoc(null); setEditingInvoice(false); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scan size={20} />
              {viewDoc?.original_filename}
            </DialogTitle>
          </DialogHeader>
          
          {viewDoc && (
            <div className="space-y-4">
              {/* Document Info */}
              <div className="grid grid-cols-2 gap-4 p-3 bg-[#F4F4F5] rounded-sm">
                <div>
                  <p className="text-xs text-[#52525B]">{t('documents.size')}</p>
                  <p className="text-sm font-medium">{formatBytes(viewDoc.size)}</p>
                </div>
                <div>
                  <p className="text-xs text-[#52525B]">{t('documents.date')}</p>
                  <p className="text-sm font-medium">
                    {formatDate(viewDoc.created_at, localeMap[language])}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#52525B]">{t('documents.pdfa')}</p>
                  <p className="text-sm font-medium flex items-center gap-1">
                    {viewDoc.has_pdfa ? (
                      <>
                        <CheckCircle size={14} className="text-green-600" weight="fill" />
                        <span className="text-green-700">PDF/A-1b</span>
                      </>
                    ) : (
                      <span className="text-[#A1A1AA]">—</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#52525B]">{t('documents.tags')}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {viewDoc.tags?.length > 0 ? (
                      viewDoc.tags.map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="text-[10px]">
                          {tag}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-[#A1A1AA]">—</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Invoice Data */}
              {viewDoc.invoice_data && Object.keys(viewDoc.invoice_data).length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                      <Receipt size={14} className="inline mr-1" />
                      {t('scanner.invoiceData')}
                    </Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingInvoice(!editingInvoice)}
                      className="text-[#002FA7]"
                    >
                      <PencilSimple size={14} className="mr-1" />
                      {editingInvoice ? t('common.cancel') : t('scanner.editInvoiceData')}
                    </Button>
                  </div>
                  
                  <div className={editingInvoice ? "grid grid-cols-2 gap-3" : ""}>
                    {['supplier', 'invoiceNumber', 'invoiceDate', 'dueDate', 'subtotal', 'vatRate', 'vatAmount', 'totalAmount', 'currency', 'iban'].map((key) => {
                      const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
                      return renderInvoiceField(key, viewDoc.invoice_data[snakeKey]);
                    })}
                  </div>
                  
                  {editingInvoice && (
                    <div className="flex justify-end mt-4">
                      <Button
                        onClick={handleSaveInvoiceData}
                        disabled={saving}
                        className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
                      >
                        {saving ? t('common.loading') : t('common.save')}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* OCR Text */}
              {viewDoc.ocr_text && (
                <div>
                  <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-2 block">
                    {t('scanner.ocrResult')}
                  </Label>
                  <Textarea
                    value={viewDoc.ocr_text}
                    readOnly
                    className="h-40 text-xs font-mono bg-[#F4F4F5]"
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            {viewDoc?.has_pdfa && (
              <Button
                onClick={() => api.downloadPdfA(viewDoc.id, viewDoc.original_filename)}
                variant="outline"
                className="rounded-sm"
              >
                <Archive size={18} className="mr-2" />
                {t('documents.downloadPdfa')}
              </Button>
            )}
            <Button
              onClick={() => api.downloadDocument(viewDoc.id, viewDoc.original_filename)}
              className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
            >
              {t('documents.download')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
